/**
 * Access-requests admin API — generated-client adapter (Task 11 migration).
 *
 * The Access-Requests endpoints are served by the genapi-generated routes; this
 * hook wraps the generated ApicClient (which carries the Bearer via
 * config.headers and hits the production /v1/admin/tenants/{t}/access-requests/*
 * routes) behind the same method surface the pages already use.
 *
 * Response casting: most access-request CRUD endpoints use the generated
 * AdminAccessRequestResp shape while the activation endpoints use the dedicated
 * PamAccessRequestActivationResp token/grant contract. CRUD responses are
 * converted to the hand-written snake_case types the pages render; activation
 * responses are intentionally consumed only as success/failure here because
 * this UI does not retain the one-shot DPoP private key needed to present the
 * returned sender-constrained token.
 */
import { useMemo } from 'react'
import { useAdminGenClient, useAdminGenClientNoAutoLogout } from './genClient'
import { generateDPoPProof } from '../auth/dpop'
import type {
  AccessRequest,
  AccessRequestSubmitBody,
  EvidencePackage,
  EvidenceRecord,
} from './types'

export interface AccessRequestsApi {
  list(tenantId: string, cursor?: string): Promise<{ items: AccessRequest[]; next_cursor?: string }>
  listBySubject(tenantId: string, subjectId: string): Promise<{ items: AccessRequest[] }>
  submit(tenantId: string, body: AccessRequestSubmitBody, idempotencyKey: string): Promise<AccessRequest>
  get(tenantId: string, requestId: string): Promise<AccessRequest>
  approve(tenantId: string, requestId: string, rationale: string): Promise<void>
  deny(tenantId: string, requestId: string, rationale: string): Promise<void>
  cancel(tenantId: string, requestId: string): Promise<void>
  extend(tenantId: string, requestId: string, newDurationSeconds: number): Promise<void>
  activate(tenantId: string, requestId: string): Promise<void>
  evidence(tenantId: string, requestId: string): Promise<EvidenceRecord>
  evidencePackage(tenantId: string, requestId: string): Promise<EvidencePackage>
}

export interface SelfServiceAccessRequestsApi {
  get(requestId: string): Promise<AccessRequest>
  activate(requestId: string): Promise<void>
}

interface ListWire {
  items?: AccessRequest[]
  next_cursor?: string
}

/**
 * Deep camelCase → snake_case key conversion.
 *
 * The generated ApicClient runs every response body through `fromWire`, which
 * camelCases all keys (ticket_ref → ticketRef, subject_id → subjectId,
 * next_cursor → nextCursor, …). The hand-written AccessRequest / EvidenceRecord
 * types and the pages that render them (AccessRequestDetail, EvidenceView,
 * AccessRequests) read snake_case fields. Without this conversion the detail page
 * crashes on `req.ticket_ref.system` (ticket_ref is undefined) and pagination
 * silently breaks (next_cursor is undefined). Sibling adapters (Clients,
 * Resources) do the same camel→snake remap field-by-field; this is the generic
 * form. Mirrors the inverse of the client's snakeToCamel.
 */
function camelToSnakeKey(key: string): string {
  return key.replace(/[A-Z]/g, (m) => '_' + m.toLowerCase())
}

function deepSnake<T>(value: unknown): T {
  if (Array.isArray(value)) {
    return value.map((v) => deepSnake(v)) as unknown as T
  }
  if (
    value !== null &&
    typeof value === 'object' &&
    !(value instanceof Date) &&
    !(typeof Blob !== 'undefined' && value instanceof Blob)
  ) {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        camelToSnakeKey(k),
        deepSnake(v),
      ])
    ) as T
  }
  return value as T
}

/**
 * useAccessRequestsApi returns an AccessRequestsApi bound to the admin gen
 * client. Recreated only when the underlying client (token) changes.
 */
export function useAccessRequestsApi(): AccessRequestsApi {
  const client = useAdminGenClient()
  // activate() alone routes through the no-auto-logout client: a 401 here
  // means "step-up required" (recoverable), not "your session is dead" — see
  // useAdminGenClientNoAutoLogout's doc comment in genClient.ts.
  const activateClient = useAdminGenClientNoAutoLogout()

  return useMemo<AccessRequestsApi>(
    () => ({
      async list(tenantId, cursor) {
        const r = deepSnake<ListWire>(
          await client.adminListAccessRequests({
            tenantId,
            cursor: cursor ?? '',
          } as never)
        )
        return { items: r.items ?? [], next_cursor: r.next_cursor }
      },
      async listBySubject(tenantId, subjectId) {
        const r = deepSnake<ListWire>(
          await client.adminListAccessRequests({
            tenantId,
            subject: subjectId,
          } as never)
        )
        return { items: r.items ?? [] }
      },
      async submit(tenantId, body, idempotencyKey) {
        const r = await client.adminCreateAccessRequest(
          { tenantId, ...body } as never,
          { headers: { 'X-Idempotency-Key': idempotencyKey } }
        )
        return deepSnake<AccessRequest>(r)
      },
      async get(tenantId, requestId) {
        const r = await client.adminGetAccessRequest({ tenantId, requestId } as never)
        return deepSnake<AccessRequest>(r)
      },
      async approve(tenantId, requestId, rationale) {
        await client.adminApproveAccessRequest({ tenantId, requestId, rationale } as never)
      },
      async deny(tenantId, requestId, rationale) {
        await client.adminDenyAccessRequest({ tenantId, requestId, rationale } as never)
      },
      async cancel(tenantId, requestId) {
        await client.adminCancelAccessRequest({ tenantId, requestId } as never)
      },
      async extend(tenantId, requestId, newDurationSeconds) {
        await client.adminExtendAccessRequest({
          tenantId,
          requestId,
          new_duration_seconds: newDurationSeconds,
        } as never)
      },
      async activate(tenantId, requestId) {
        const url =
          `${window.location.origin}/v1/admin/tenants/${tenantId}` +
          `/access-requests/${requestId}/activate`
        const proof = await generateDPoPProof('POST', url)
        await activateClient.adminActivateAccessRequest(
          { tenantId, requestId } as never,
          { headers: { DPoP: proof } }
        )
      },
      async evidence(tenantId, requestId) {
        const r = await client.adminGetAccessRequestEvidence({ tenantId, requestId } as never)
        return deepSnake<EvidenceRecord>(r)
      },
      async evidencePackage(tenantId, requestId) {
        const r = await client.adminGetAccessRequestEvidencePackage({ tenantId, requestId } as never)
        return deepSnake<EvidencePackage>(r)
      },
    }),
    [client, activateClient]
  )
}

/**
 * The cross-application activation hand-off is for the request subject, not an
 * administrator. Keep it on the ownership-checked `/v1/pam/me` surface so the
 * bare page never needs an admin role and cannot read or activate another
 * subject's request. A recoverable step-up 401 deliberately does not clear the
 * browser token.
 */
export function useSelfServiceAccessRequestsApi(): SelfServiceAccessRequestsApi {
  const client = useAdminGenClient()
  const activateClient = useAdminGenClientNoAutoLogout()

  return useMemo<SelfServiceAccessRequestsApi>(
    () => ({
      async get(requestId) {
        const r = await client.meGetAccessRequest({ requestId })
        return deepSnake<AccessRequest>(r)
      },
      async activate(requestId) {
        const url =
          `${window.location.origin}/v1/pam/me/access-requests/${requestId}/activate`
        const proof = await generateDPoPProof('POST', url)
        await activateClient.meActivateAccessRequest(
          { requestId },
          { headers: { DPoP: proof } }
        )
      },
    }),
    [client, activateClient]
  )
}
